import Component from '@glimmer/component'
import { action } from '@ember/object'
import type { Identifiable } from 'teacher-ui-client/components/data-operations/table/records-selector'
import { RecordSelector } from 'teacher-ui-client/components/data-operations/table/records-selector'
import mergeSort from '@blakeelearning/data-tables/utils/merge-sort'
import { buildComposedComparerFromComparerObjects } from '@blakeelearning/data-tables/utils/comparisons'
import type { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { SortDefinition, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import { chunk } from 'lodash'
import { hash } from '@ember/helper'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'
import type { DataOperationsConfig } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import type Owner from '@ember/owner'
import { eq } from 'ember-truth-helpers'

function flippedSortDir(sortDirection: SortDirection): SortDirection {
  return sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc
}

export interface SortingBlock<RecordType extends Identifiable> {
  sortedRecords: DataOperationsTable<RecordType>['sortedRecords']
  sort: DataOperationsTable<RecordType>['changeSort']
  sortDir: DataOperationsConfig<RecordType>['sortDir']
  sortCol: DataOperationsConfig<RecordType>['sortCol']
}

interface Args<RecordType extends Identifiable> {
  dataOperationsConfig: DataOperationsConfig<RecordType>
}

interface Signature<RecordType extends Identifiable> {
  Args: Args<RecordType>
  Blocks: {
    default: [
      RecordSelector<RecordType>,
      SortingBlock<RecordType>,
      {
        pageCount: number
        currentPage: DataOperationsConfig<RecordType>['currentPage']
        currentPageRecords: DataOperationsTable<RecordType>['currentPageRecords']
        goTo: DataOperationsTable<RecordType>['changePage']
      },
    ]
  }
}

export class DataOperationsTable<RecordType extends Identifiable> extends Component<Signature<RecordType>> {
  constructor(owner: Owner, args: Args<RecordType>) {
    super(owner, args)
    this.recordSelector = new RecordSelector(args.dataOperationsConfig)
  }

  recordSelector: RecordSelector<RecordType>

  /**
   * Provides input data records as an array. This allows the component to be provided array proxies like what is
   * returned from store.peekAll('some-record') and still work with functions that require an iterable (eg lodash)
   */
  get data(): RecordType[] {
    const { data } = this.args.dataOperationsConfig
    return Array.isArray(data) ? data : data.slice()
  }

  /**
   * Builds a sortingPriorityQueue out of the sortCol, sortDir and sortCompare that are provided
   *
   * Currently only supports _one_ sort definition, this could be extended as more are added to the config proxy & host
   * controller.
   */
  get sortingPriorityQueue() {
    const { sortCol, sortDir, sortCompare } = this.args.dataOperationsConfig

    if (!sortCol || !sortDir || !sortCompare) return []

    return [new SortDefinition({ sortCol, sortDir, sortCompare })]
  }

  /**
   * Applies the sorting configuration to a copy of the data
   */
  get sortedRecords() {
    // builds a single function out of the comparerObjects that takes two arguments
    // and gives -1, 0 or 1 (ie a standard comparer function for sorting functions)
    // the difference is that this one sorts on multiple columns
    const composedComparer = buildComposedComparerFromComparerObjects(this.sortingPriorityQueue)

    // sort a copy rather than mutate the original
    return mergeSort([...this.data], composedComparer)
  }

  /**
   * Chunk the records into pages. This is left up to lodash's chunk algorithm, we just ensure it is handed a number.
   * In the case of page size infinity, it returns all records on the first page.
   */
  get pagedRecords(): RecordType[][] {
    const pageSize = this.args.dataOperationsConfig.recordsPerPage ?? Number.POSITIVE_INFINITY
    return chunk(this.sortedRecords, pageSize)
  }

  get currentPageRecords(): RecordType[] {
    // -1 because currentPage starts at 1, and the pagedRecords is a zero-based Array
    const { currentPage = 1 } = this.args.dataOperationsConfig
    return this.pagedRecords[currentPage - 1] ?? []
  }

  @action
  changePage(newPageNumber: number) {
    this.args.dataOperationsConfig.currentPage = newPageNumber
  }

  @action
  changeRecordsPerPage(recordsPerPage: number) {
    this.args.dataOperationsConfig.recordsPerPage = recordsPerPage
    this.args.dataOperationsConfig.currentPage = 1
  }

  /**
   * Update sort configuration, this will trigger changes to sortingPriorityQueue, and then sortedRecords
   */
  @action
  changeSort(sortCompare: SortType, sortCol: string) {
    if (this.args.dataOperationsConfig.sortCol === sortCol && this.args.dataOperationsConfig.sortDir) {
      // flip & set sort dir
      this.args.dataOperationsConfig.sortDir = flippedSortDir(this.args.dataOperationsConfig.sortDir)
    } else {
      // set new sortcol, set sortdir to asc
      this.args.dataOperationsConfig.sortCol = sortCol
      this.args.dataOperationsConfig.sortDir = SortDirection.Asc
    }

    // set the comparer if it changed
    if (this.args.dataOperationsConfig.sortCompare !== sortCompare) {
      this.args.dataOperationsConfig.sortCompare = sortCompare
    }
  }

  <template>
    {{yield
      this.recordSelector
      (hash
        sortedRecords=this.sortedRecords
        sort=this.changeSort
        sortDir=@dataOperationsConfig.sortDir
        sortCol=@dataOperationsConfig.sortCol
      )
      (hash
        pageCount=this.pagedRecords.length
        currentPage=@dataOperationsConfig.currentPage
        currentPageRecords=this.currentPageRecords
        goTo=this.changePage
      )
    }}
    {{#unless (eq @dataOperationsConfig.recordsPerPage Number.POSITIVE_INFINITY)}}
      <BasePageSelector
        class="mt-4"
        @pageCount={{this.pagedRecords.length}}
        @currentPage={{@dataOperationsConfig.currentPage}}
        @changePage={{this.changePage}}
        @itemsPerPage={{@dataOperationsConfig.recordsPerPage}}
        @changePerPage={{this.changeRecordsPerPage}}
        @itemCount={{this.sortedRecords.length}}
      />
    {{/unless}}
  </template>
}

export default DataOperationsTable
